import { keepPreviousData as keepPreviousDataFunction, useQuery } from '@tanstack/react-query'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { useFireSessionContext } from '@chilipiper/fire-session'
import type WorkspaceClient from '@chilipiper/api-client/src/workspace'
import type FireRouterClient from '@chilipiper/api-client/src/firerouter'
import { MapUserIdPersonalDetails, PersonalDetails } from '@chilipiper/api-client/src/workspace'

const queryKey = 'personal-details'
export const queryKeyFactory = {
  findPersonalDetailsByName: (tenantId: string, name: string) => [
    queryKey,
    'tenant',
    tenantId,
    'name',
    name,
  ],
  findPersonalDetails: (tenantId: string, userId: string) => [
    queryKey,
    'tenant',
    tenantId,
    'user',
    userId,
  ],
  getPicture: (tenantId: string, userId: string) => [
    queryKey,
    'profile-picture',
    'tenant',
    tenantId,
    'user',
    userId,
  ],
  findPersonalDetailsByIds: (tenantId: string, userIds: string[]) => [tenantId, ...userIds],
}

export const useFindPersonalDetailsByName = (name: string, isEnabled = true) => {
  const { workspace } = useApiClient()
  const { tenantId } = useFireSessionContext()
  return useQuery({
    queryKey: queryKeyFactory.findPersonalDetailsByName(tenantId, name),
    queryFn: () => workspace.findPersonalDetailsByName(tenantId, { name }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousDataFunction,
    enabled: isEnabled,
  })
}

export const usePersonalDetails = (userId = '') => {
  const { workspace } = useApiClient()
  const { tenantId } = useFireSessionContext()
  return useQuery({
    queryKey: queryKeyFactory.findPersonalDetails(tenantId, userId),
    queryFn: () => workspace.findPersonalDetailsForGuests(tenantId, userId),
    retryOnMount: false,
    retry: (_count, error) => 'status' in error && error.status !== 404,
    refetchOnWindowFocus: false,
    enabled: userId !== '',
  })
}

export type PersonalDetailsWithId = MapUserIdPersonalDetails[string] & { id: string }
export const useFindPersonalDetailsByIds = (
  tenantId: string,
  userIds: string[],
  { keepPreviousData }: { keepPreviousData?: boolean } = {}
) => {
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.findPersonalDetailsByIds(tenantId, userIds),
    queryFn: async () => {
      const users = await workspace.findPersonalDetailsOfListOfUsersForGuests(tenantId, {
        id: userIds,
      })
      return Object.fromEntries(
        userIds.map(userId => {
          if (users[userId]) {
            return [
              userId,
              {
                ...users[userId],
                id: userId,
              } as PersonalDetailsWithId,
            ]
          }

          return [userId, null]
        })
      )
    },
    enabled: Boolean(userIds.length),
    placeholderData: keepPreviousData ? keepPreviousDataFunction : undefined,
    staleTime: Infinity,
  })
}

export const useFindPersonalDetailsWithSlugsByIds = (userIds: string[]) => {
  const { workspace, firerouter } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useQuery({
    queryKey: queryKeyFactory.findPersonalDetailsByIds(tenantId, userIds),
    queryFn: () =>
      handlePersonalDetailsWithSlug({
        workspaceClient: workspace,
        fireRouterClient: firerouter,
        tenantId,
        userIds,
      }),
    enabled: Boolean(userIds.length),
    staleTime: Infinity,
  })
}

export type PersonalDetailsWithSlug = PersonalDetails & {
  id: string
  slug: string
}

type HandlePersonalDetailsWithSlug = {
  fireRouterClient: FireRouterClient
  tenantId: string
  userIds: string[]
  workspaceClient: WorkspaceClient
}

const handlePersonalDetailsWithSlug = async ({
  workspaceClient,
  fireRouterClient,
  tenantId,
  userIds,
}: HandlePersonalDetailsWithSlug): Promise<Record<string, PersonalDetailsWithSlug>> => {
  const users = await workspaceClient.findPersonalDetailsOfListOfUsersForGuests(tenantId, {
    id: userIds,
  })
  const result = await Promise.allSettled(
    Object.keys(users).map(async userId => {
      const slug = await fireRouterClient.getSlugsForUserId(tenantId, userId)
      return {
        ...users[userId],
        id: userId,
        slug,
      }
    })
  )

  const usersWithSlugs = result.filter(isFulfilled).reduce((users, user) => {
    return {
      ...users,
      [user.value.id]: user.value,
    }
  }, {})

  return usersWithSlugs
}

const isFulfilled = <T>(result: PromiseSettledResult<T>): result is PromiseFulfilledResult<T> =>
  result.status === 'fulfilled'
